import { AlertCircle, CheckCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { IScenario } from '@/types';

interface CallInstructionsProps {
  scenario: IScenario;
}

const CallInstructions = ({ scenario }: CallInstructionsProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{scenario.name}</CardTitle>
        <CardDescription>{scenario.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="customer">Customer</TabsTrigger>
            <TabsTrigger value="objections">Objections</TabsTrigger>
            <TabsTrigger value="scripts">Scripts</TabsTrigger>
            <TabsTrigger value="tips">Tips</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <div className="space-y-4">
              <div>
                <h3 className="mb-2 font-semibold">Call Objectives:</h3>
                <ul className="pl-5 list-disc">
                  {scenario.objective.split('\n').map((objective, index) => (
                    <li key={index}>{objective}</li>
                  ))}
                </ul>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="customer">
            <div className="space-y-4">
              <h3 className="mb-2 font-semibold">Call Data:</h3>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(scenario.metadata).map(([key, value], index) => (
                  <div key={key + index} className="flex items-center space-x-2">
                    <span className="font-medium">{key}:</span>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
          <TabsContent value="objections">
            <div className="space-y-4">
              <h3 className="mb-2 font-semibold">Potential Objections:</h3>
              <ul className="space-y-2">
                {scenario.potential_objections.split('\n').map((objection, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
                    <span>{objection}</span>
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="scripts">
            <div className="space-y-4">
              <h3 className="mb-2 font-semibold">Recommended Scripts:</h3>
              <Accordion type="single" collapsible className="w-full">
                {Object.entries(scenario.scripts).map(([stage, script], index) => (
                  <AccordionItem value={`item-${index}`} key={index}>
                    <AccordionTrigger>{stage}</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-sm text-muted-foreground">{script}</p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </TabsContent>
          <TabsContent value="tips">
            <div className="space-y-4">
              <h3 className="mb-2 font-semibold">Tips for Success:</h3>
              <ul className="space-y-2">
                {scenario.tips.split('\n').map((tip, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default CallInstructions;
